import { useEffect, useState } from 'react'
import { Deferred, Head, router, usePage } from '@inertiajs/react'
import { FilePlus2, Upload } from 'lucide-react'
import { Button } from '@/components/ui/button'
import DashboardLayout from '@/frontend/layouts/DashboardLayout'
import DocumentUploadDialog from '@/frontend/features/Fde/DocumentUploadDialog'
import FdeShell from '@/frontend/features/Fde/FdeShell'
import ForgePane from '@/frontend/features/Fde/ForgePane'
import ForgeStage from '@/frontend/features/Fde/ForgeStage'
import ProjectControls from '@/frontend/features/Fde/ProjectControls'
import ProjectDialog from '@/frontend/features/Fde/ProjectDialog'
import ProjectKnowledgeExplorer, { ProjectKnowledgeLoading } from '@/frontend/features/Fde/ProjectKnowledgeExplorer'
import RepositoryPageDialog from '@/frontend/features/Fde/RepositoryPageDialog'
import WorkspaceBrowser from '@/frontend/features/Fde/WorkspaceBrowser'
import WorkspaceFilePanel from '@/frontend/features/Fde/WorkspaceFilePanel'
import WorkspaceModeTabs from '@/frontend/features/Fde/WorkspaceModeTabs'
import useWorkspaceTabs from '@/frontend/features/Fde/useWorkspaceTabs'
import type { MaterialProps, Project, ProjectKnowledgeSource } from '@/frontend/features/Fde/types'
import { fdeProjectPath, fdeRoutes } from '@/frontend/features/Fde/routes'
import type { SharedProps } from '@/frontend/types/inertia'

function MaterialPage(props: MaterialProps) {
  const shared = usePage<SharedProps>().props
  const contextualNavigation = shared.featureFlags?.worker_first_nav_enabled && shared.permissions?.can_access_forge
  const [uploadOpen, setUploadOpen] = useState(false)
  const [pageOpen, setPageOpen] = useState(false)
  const [projectDialog, setProjectDialog] = useState<{ open: boolean; project: Project | null }>({ open: false, project: null })
  const projectId = props.project?.id || ''
  const [workspaceView, setWorkspaceView] = useState<'files' | 'knowledge'>(props.workspace_view || 'files')
  const [knowledgeRequested, setKnowledgeRequested] = useState(false)
  const tabs = useWorkspaceTabs(projectId, props.files, props.selected_file?.path)
  const actions = <ProjectControls path={fdeRoutes.material} project={props.project} projects={props.project_history} copy={props.copy} canManage={props.can_manage} onEdit={() => setProjectDialog({ open: true, project: props.project })} onNew={() => setProjectDialog({ open: true, project: null })} />

  useEffect(() => {
    setWorkspaceView(props.workspace_view || 'files')
    setKnowledgeRequested(false)
  }, [projectId, props.workspace_view])

  useEffect(() => {
    if (props.project?.repository_sync_status !== 'pending') return undefined

    const intervalId = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return
      router.reload({
        only: ['project', 'files', 'selected_file', 'file_versions', 'selected_revision', 'repository_revision', 'project_memory'],
      })
    }, 3000)
    return () => window.clearInterval(intervalId)
  }, [props.project?.repository_sync_status, props.project?.repository_synced_at])

  function visitFile(path?: string, revision?: string) {
    if (!projectId) return
    setWorkspaceView('files')
    if (path) tabs.open(path)
    router.visit(fdeProjectPath(fdeRoutes.material, projectId, { file: path, revision }), {
      preserveScroll: true,
      preserveState: true,
      only: ['selected_file', 'file_versions', 'selected_revision', 'repository_revision'],
    })
  }

  function changeWorkspaceView(view: 'files' | 'knowledge') {
    setWorkspaceView(view)
    if (view !== 'knowledge' || props.project_knowledge || knowledgeRequested) return

    setKnowledgeRequested(true)
    router.reload({
      only: ['project_knowledge'],
      onFinish: () => setKnowledgeRequested(false),
    })
  }

  function openKnowledgeSource(source: ProjectKnowledgeSource) {
    if (source.path.startsWith('whatsapp:')) {
      router.get('/dashboard/discovery/project_whatsapp_sources/message', { project_id: projectId, source_ref: source.path })
      return
    }
    visitFile(source.path, source.revision || undefined)
  }

  function closeFile(path: string) {
    const remaining = tabs.openFiles.filter((file) => file.path !== path)
    tabs.close(path)
    if (props.selected_file?.path === path) visitFile(remaining.at(-1)?.path)
  }

  const workspaceActions = props.project && <div className="flex items-center gap-2">
    {!contextualNavigation && <WorkspaceModeTabs value={workspaceView} copy={props.copy} onChange={changeWorkspaceView} />}
    {workspaceView === 'files' && props.can_manage && <Button size="sm" variant="outline" onClick={() => setUploadOpen(true)}><Upload />{props.copy.material.upload}</Button>}
    {workspaceView === 'files' && props.can_manage && <Button size="sm" onClick={() => setPageOpen(true)}><FilePlus2 />{props.copy.material.new_file}</Button>}
  </div>

  return <>
    <Head title={props.page_title} />
    <FdeShell copy={props.copy} eyebrow={props.copy.material.eyebrow} title={props.project?.name ?? props.copy.material.title} description={props.copy.material.description} actions={actions} projectId={props.project?.id}>
      <ForgeStage props={props} primaryLabel={props.copy.navigation.workspace} refreshProps={['files', 'selected_file', 'file_versions', 'repository_revision', 'project_memory', 'project_knowledge']} renderPrimary={(headerActions) => <ForgePane title={contextualNavigation ? shared.navigationLabels.forge[workspaceView === 'knowledge' ? 'memory' : 'files'] : workspaceView === 'knowledge' ? props.copy.material.knowledge_title : props.copy.material.title} description={workspaceView === 'knowledge' ? props.copy.material.knowledge_description : props.copy.material.description} headerActions={headerActions} actions={workspaceActions}>
        {props.project && <>
          <div className={workspaceView === 'files' ? 'h-full min-h-0' : 'hidden'}>{props.selected_file ? <WorkspaceFilePanel
            projectId={projectId}
            file={props.selected_file}
            openFiles={tabs.openFiles}
            repositoryRevision={props.repository_revision}
            selectedRevision={props.selected_revision}
            versions={props.file_versions}
            copy={props.copy}
            editable={props.can_manage}
            onBack={() => visitFile()}
            onSelect={visitFile}
            onClose={closeFile}
          /> : <WorkspaceBrowser files={props.files} copy={props.copy} onOpen={visitFile} />}</div>
          <div className={workspaceView === 'knowledge' ? 'h-full min-h-0' : 'hidden'}><Deferred data="project_knowledge" fallback={<ProjectKnowledgeLoading copy={props.copy} />}>
            {props.project_knowledge ? <ProjectKnowledgeExplorer projectId={projectId} knowledge={props.project_knowledge} memory={props.project_memory} copy={props.copy} canManage={props.can_manage} onOpenSource={openKnowledgeSource} /> : <ProjectKnowledgeLoading copy={props.copy} />}
          </Deferred></div>
        </>}
      </ForgePane>} />
    </FdeShell>
    <DocumentUploadDialog open={uploadOpen} onOpenChange={setUploadOpen} copy={props.copy} projectId={props.project?.id} />
    <RepositoryPageDialog open={pageOpen} onOpenChange={setPageOpen} copy={props.copy} projectId={props.project?.id} repositoryRevision={props.repository_revision} />
    <ProjectDialog open={projectDialog.open} onOpenChange={(open) => setProjectDialog((current) => ({ ...current, open }))} project={projectDialog.project} agents={props.agents} copy={props.copy} activeProjectId={props.project?.id} />
  </>
}

MaterialPage.layout = (page: React.ReactElement) => <DashboardLayout variant="fullscreen">{page}</DashboardLayout>
export default MaterialPage
